import {ChangeDetectorRef, Component, inject, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    DxButtonComponent,
    DxCheckBoxModule,
    DxFormModule,
    DxLoadPanelComponent,
    DxRadioGroupModule,
    DxScrollViewComponent,
    DxTextBoxModule,
    DxToolbarComponent,
    DxTreeListComponent,
    DxTreeListModule,
    DxValidatorModule
} from 'devextreme-angular';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {forkJoin, of} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import EnumUtils from '../../../../shared/enum-utils';
import {
    AccessType,
    GetRoleQuery,
    getSizeQualifier,
    PermissionDto,
    PermissionGroup,
    PermissionLine,
    RoleDto,
    RolesClient,
    UpdateRoleCommand
} from '@services';
import notify from 'devextreme/ui/notify';
import {ProfileCardComponent} from "../../../../components";
import {AuthService} from '@services';
import {PermissionTypePipe} from "../../../../pipes/permission-type.pipe";

interface PermissionTreeNode {
    id: string;
    name: string;
    parentId: string | null;
    isGroup?: boolean;
    permissionType?: string;
    options: {
        value: AccessType
        text: string,
        disabled: boolean
    }[];
}

@Component({
    selector: 'role-form',
    templateUrl: './edit-role-form.component.html',
    styleUrls: ['./edit-role-form.component.scss'],
    providers: [PermissionTypePipe],
    imports: [
        DxTextBoxModule,
        DxFormModule,
        DxValidatorModule,
        DxTreeListModule,
        CommonModule,
        DxRadioGroupModule,
        DxCheckBoxModule,
        FormsModule,
        DxToolbarComponent,
        DxLoadPanelComponent,
        DxScrollViewComponent,
        ProfileCardComponent,
        DxButtonComponent
    ]
})
export class EditRoleFormComponent implements OnInit {
    private ref = inject(ChangeDetectorRef);
    private rolesClient = inject(RolesClient);
    private authService = inject(AuthService);
    private route = inject(ActivatedRoute);
    private permissionTypePipe = inject(PermissionTypePipe);

    private savedFormData: RoleDto;
    private savedPermissionSelections: { [permissionType: string]: AccessType } = {};

    protected readonly getSizeQualifier = getSizeQualifier;
    protected readonly screen = screen;
    protected isContentScrolled: boolean;
    protected isDataChanged: boolean;
    protected formData: RoleDto = {} as RoleDto;
    protected accessOptions = EnumUtils.getEnumOptions(AccessType);
    protected permissionHierarchy: PermissionGroup[] = [];
    protected permissionSelections: { [permissionType: string]: AccessType } = {};
    protected isLoading = false;
    protected permissionTree: PermissionTreeNode[] = [];

    @ViewChild(DxTreeListComponent, {static: false}) treeList?: DxTreeListComponent;

    private buildPermissionTree(): PermissionTreeNode[] {
        const nodes: PermissionTreeNode[] = [];
        for (const g of (this.permissionHierarchy || [])) {
            const groupId = `g_${g.name}`;
            nodes.push({id: groupId, name: g.name, isGroup: true, parentId: null, options: []});
            if (g.permissionLines) {
                for (const line of g.permissionLines) {
                    nodes.push({
                        id: `${groupId}__${line.permissionType}`,
                        parentId: groupId,
                        name: this.permissionTypePipe.transform(line.permissionType),
                        permissionType: line.permissionType,
                        isGroup: false,
                        options: this.getAvailableAccessOptions(line.excludeAccessTypes || [])
                    });
                }
            }
        }
        return nodes;
    }

    private mapPermissionsFromDto() {
        for (const p of this.formData.permissions) {
            this.permissionSelections[p.permissionType] = p.accessType;
        }

        for (const group of this.permissionHierarchy) {
            for (const line of group.permissionLines) {
                const key = line.permissionType as string;
                if (!this.permissionSelections[key]) {
                    this.permissionSelections[key] = AccessType.Hidden;
                }
            }
        }
    }

    private clone<T>(profile: T): T {
        return profile ? JSON.parse(JSON.stringify(profile)) : null;
    }

    private getAvailableAccessOptions(excluded: AccessType[]): {
        value: AccessType
        text: string,
        disabled: boolean
    }[] {
        return this.accessOptions.map(i => {
            let disabled = excluded.indexOf(i.value) >= 0
            return {...i, disabled: disabled};
        });
    }

    ngOnInit(): void {
        this.route.paramMap.pipe(
            map(params => params.get('roleId') || params.get('id')),
            filter(roleId => !!roleId),
            tap(() => this.isLoading = true),
            switchMap((roleId) =>
                forkJoin({
                    hierarchy: this.rolesClient.getPermissionHierarchy(),
                    role: this.rolesClient.getRole(new GetRoleQuery({roleId})).pipe(catchError(() => of(null)))
                })
            )
        ).subscribe({
            next: ({hierarchy, role}) => {
                this.permissionHierarchy = hierarchy.data;
                this.formData = role;
                this.savedFormData = this.clone(this.formData);
                this.mapPermissionsFromDto();
                this.savedPermissionSelections = this.clone(this.permissionSelections);
                this.permissionTree = this.buildPermissionTree();
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    getGroupByName(groupName: string): PermissionGroup | undefined {
        return this.permissionHierarchy.find(g => g.name === groupName);
    }

    getGroupLines(groupName: string): PermissionLine[] {
        const group = this.getGroupByName(groupName);
        return group?.permissionLines || [];
    }

    getApplicableLinesForAccess(groupName: string, accessType: AccessType): PermissionLine[] {
        return this.getGroupLines(groupName).filter(line => !(line.excludeAccessTypes || []).includes(accessType));
    }

    countGroupSelected(groupName: string, accessType: AccessType): number {
        const lines = this.getApplicableLinesForAccess(groupName, accessType);
        return lines.reduce((count, line) => {
            const sel = this.permissionSelections[line.permissionType as string];
            return count + (sel === accessType ? 1 : 0);
        }, 0);
    }

    isGroupChecked(groupName: string, accessType: AccessType): boolean {
        const lines = this.getApplicableLinesForAccess(groupName, accessType);
        if (lines.length === 0) return false;
        return lines.every(line => this.permissionSelections[line.permissionType as string] === accessType);
    }

    toggleGroupAccess(groupName: string, accessType: AccessType, e: any) {
        // Prevent update loop when individual lines change (programmatic change vs user interaction)
        if (!e.event) return;

        const checked = e.value;
        const lines = this.getApplicableLinesForAccess(groupName, accessType);
        if (!lines || lines.length === 0) return;
        for (const line of lines) {
            const key = line.permissionType as string;
            if (checked) {
                this.permissionSelections[key] = accessType;
            } else {
                if (this.permissionSelections[key] === accessType) {
                    this.permissionSelections[key] = AccessType.Hidden;
                }
            }
        }
        this.isDataChanged = true;
    }

    onPermissionValueChanged(permissionType: string, newValue: AccessType) {
        this.permissionSelections[permissionType] = newValue;
        this.isDataChanged = true;
    }

    save = () => {
        const command = this.getCommand();
        this.rolesClient.updateRole(command).subscribe({
            next: async () => {
                await this.authService.refresh();

                this.savedFormData = this.clone(this.formData);
                this.savedPermissionSelections = this.clone(this.permissionSelections);
                this.isDataChanged = false;

                notify({
                    message: 'Role saved',
                    type: 'success',
                    displayTime: 3000
                }, {
                    position: 'bottom right',
                    direction: 'up-push'
                });
            },
            error: (errors: Array<any>) => {
                (errors || []).forEach(err => {
                    notify({
                        message: err?.message || err,
                        type: 'error',
                        displayTime: 5000
                    }, {
                        position: 'bottom right',
                        direction: 'up-push'
                    });
                });
            }
        });
    }

    getCommand(): UpdateRoleCommand {
        const accessArray: PermissionDto[] = [];
        for (const key of Object.keys(this.permissionSelections || {})) {
            accessArray.push(new PermissionDto({
                permissionType: key as any,
                accessType: this.permissionSelections[key]
            }));
        }

        return new UpdateRoleCommand({
            roleId: this.formData.id,
            name: this.formData.name,
            description: this.formData.description,
            access: accessArray
        });
    }

    scroll({reachedTop = false}) {
        this.isContentScrolled = !reachedTop;
    }

    cancel() {
        if (this.savedFormData) {
            this.formData = this.clone(this.savedFormData);
            this.permissionSelections = this.clone(this.savedPermissionSelections);
        }
        this.isDataChanged = false;
        this.ref.detectChanges();
    }

    async onTreeRowDblClick(e: { data: PermissionTreeNode }) {
        const data = e.data;
        if (data.isGroup) {
            const key = data.id;
            let instance = this.treeList.instance;
            if (instance.isRowExpanded(key))
                await instance.collapseRow(key);
            else
                await instance.expandRow(key);
        }
    }

    getCellData(cell: { data: PermissionTreeNode }) {
        return cell.data;
    }

    protected onFieldChange() {
        this.isDataChanged = true;
    }
}
